import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Box, Container, Typography, Backdrop, CircularProgress } from '@mui/material'
import HoldAllocationNav from '../components/HoldAllocationNav/hold-allocation-nav'
import BillingDetailsCardV2 from './components/BillingDetailsCard/billing-details-card-v2'
import ShippingDetailsCardV2 from './components/ShippingDetailsCard/shipping-details-card-v2'
import OrderSummaryCardV2 from './components/OrderSummaryCard/order-summary-card-v2'
import ShipInstructionsBarV2 from './components/ShipInstructionsBar/ship-instructions-bar-v2'
import CustomerCreditInfoBarV2 from './components/CustomerCreditInfoBar/customer-credit-info-bar-v2'
import OrderLineDetailsTableV2 from './components/OrderLineDetailsTable/order-line-details-table-v2'
import HoldDetailsTableV2 from './components/HoldDetailsTableV2/hold-details-table-v2'
import AllocationDetailsTableV2 from './components/AllocationDetailsTableV2/allocation-details-table-v2'
import GenericSnackbar from '../../../components/GenericSnackbar/generic-snackbar'
import { setSnackbar } from '../../../components/GenericSnackbar/generic-snackbar-slice'
import {
  fetchOrderDetail,
  doReleaseHold,
  doReleaseAll,
  doReapplyHold,
  doSubmitAndVerifyAllocation,
  doPrefillAllocation,
  resetDetail,
} from '../hold-allocation-detail-slice'
import { getOrders } from '../../../api/holdallocation/hold-allocation-api'

// Maps API errorCode -> { message, severity, refetch }
// refetch=true means the UI is stale and we should reload the order detail
// after surfacing the message.
const ERROR_CODE_MAP = {
  BAD_REQUEST: {
    message: 'Comment is required and must be 36 characters or fewer.',
    severity: 'error',
    refetch: false,
  },
  RBAC_DENIED: {
    message: 'Your role is not permitted to perform this action.',
    severity: 'error',
    refetch: false,
  },
  ORDER_NOT_FOUND: { message: 'Order no longer exists.', severity: 'error', refetch: false },
  HOLD_NOT_FOUND: {
    message: 'Hold no longer exists — refreshing.',
    severity: 'warning',
    refetch: true,
  },
  HOLD_ALREADY_RELEASED: {
    message: 'This hold is already released — refreshing.',
    severity: 'warning',
    refetch: true,
  },
  HOLD_ALREADY_ACTIVE: {
    message: 'This hold is already active — refreshing.',
    severity: 'warning',
    refetch: true,
  },
  TECSYS_REFUSED: {
    message: 'The upstream system refused the change. Please try again shortly.',
    severity: 'error',
    refetch: false,
  },
  MUTATIONS_DISABLED: {
    message: 'Hold mutations are temporarily disabled.',
    severity: 'warning',
    refetch: false,
  },
  PARTIAL_RELEASE: {
    message: 'Some holds could not be released.',
    severity: 'warning',
    refetch: true,
  },
}

const resolveApiError = (errPayload, fallback = 'Something went wrong.') => {
  if (!errPayload) return { message: fallback, severity: 'error', refetch: false }
  // Plain string (legacy paths)
  if (typeof errPayload === 'string')
    return { message: errPayload, severity: 'error', refetch: false }
  const mapped = errPayload.errorCode && ERROR_CODE_MAP[errPayload.errorCode]
  if (mapped) return mapped
  return { message: errPayload.message || fallback, severity: 'error', refetch: false }
}

function HoldAllocationDetailV2() {
  const { orderId } = useParams()
  const [searchParams] = useSearchParams()
  const bucket = searchParams.get('bucket') || ''
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const orderDetail = useSelector((s) => s.holdAllocationDetail.orderDetail)
  const holds = useSelector((s) => s.holdAllocationDetail.holds)
  const allocationLines = useSelector((s) => s.holdAllocationDetail.allocationLines)
  const isFetching = useSelector((s) => s.holdAllocationDetail.isFetching)
  const isSubmitting = useSelector((s) => s.holdAllocationDetail.isSubmitting)
  const error = useSelector((s) => s.holdAllocationDetail.error)
  const submitError = useSelector((s) => s.holdAllocationDetail.submitError)

  const [mockRole, setMockRole] = useState('CUSTOMER_SERVICE')
  const [mockOrg, setMockOrg] = useState('')

  const loadDetail = useCallback(() => {
    if (orderId) {
      dispatch(fetchOrderDetail({ orderId, role: mockRole, org: mockOrg }))
    }
  }, [dispatch, orderId, mockRole, mockOrg])

  useEffect(() => {
    loadDetail()
    return () => {
      dispatch(resetDetail())
    }
  }, [loadDetail, dispatch])

  useEffect(() => {
    if (error) {
      dispatch(
        setSnackbar({ message: error, severity: 'error', action: true, autoHideDuration: 4000 }),
      )
    }
  }, [error, dispatch])

  // submitError fallback (only fires if a handler below didn't already surface it)
  useEffect(() => {
    if (submitError) {
      const resolved = resolveApiError(submitError)
      dispatch(
        setSnackbar({
          message: resolved.message,
          severity: resolved.severity,
          action: true,
          autoHideDuration: 4000,
        }),
      )
    }
  }, [submitError, dispatch])

  const surfaceMutationError = (errPayload, fallback) => {
    const resolved = resolveApiError(errPayload, fallback)
    dispatch(
      setSnackbar({
        message: resolved.message,
        severity: resolved.severity,
        action: true,
        autoHideDuration: 4500,
      }),
    )
    if (resolved.refetch) loadDetail()
  }

  // ─── Hold release handlers ───────────────────────────
  const handleReleaseSingle = async (hold, comment) => {
    const result = await dispatch(
      doReleaseHold({
        orderId,
        holdId: hold.holdId,
        role: mockRole,
        username: 'J. Anderson',
        comment: comment || 'Released',
      }),
    )
    if (result.error) {
      surfaceMutationError(result.payload, `Failed to release ${hold.holdCode}.`)
      return
    }
    dispatch(
      setSnackbar({
        message: `Hold ${hold.holdCode} released`,
        severity: 'success',
        action: true,
        autoHideDuration: 3000,
      }),
    )
    loadDetail()
  }

  const handleReleaseAll = async (comment) => {
    const result = await dispatch(
      doReleaseAll({
        orderId,
        role: mockRole,
        username: 'J. Anderson',
        comment: comment || 'Batch release',
      }),
    )
    if (result.error) {
      surfaceMutationError(result.payload, 'Failed to release holds.')
      return
    }
    const summary = result.payload || {}
    if (summary.partial) {
      dispatch(
        setSnackbar({
          message: `${summary.released || 0} released, ${summary.locked || 0} locked, ${summary.failed || 0} failed.`,
          severity: 'warning',
          action: true,
          autoHideDuration: 5000,
        }),
      )
    } else {
      dispatch(
        setSnackbar({
          message: summary.released
            ? `${summary.released} hold${summary.released === 1 ? '' : 's'} released`
            : 'All releasable holds released',
          severity: 'success',
          action: true,
          autoHideDuration: 3000,
        }),
      )
    }
    // Always refetch to pick up server-side releaseReferenceIds and any side effects.
    loadDetail()
  }

  // ─── Re-apply hold handler (real API) ────────────────
  const handleReapplyHold = async (hold, applyComment) => {
    const result = await dispatch(
      doReapplyHold({
        orderId,
        holdId: hold.holdId,
        holdCode: hold.holdCode,
        applyComment: applyComment || '',
        role: mockRole,
        username: 'J. Anderson',
      }),
    )
    if (result.error) {
      surfaceMutationError(result.payload, `Failed to re-apply ${hold.holdCode}.`)
      return
    }
    dispatch(
      setSnackbar({
        message: `Hold ${hold.holdCode} re-applied`,
        severity: 'success',
        action: true,
        autoHideDuration: 3000,
      }),
    )
    loadDetail()
  }

  // ─── Allocation handlers ─────────────────────────────
  // Uses the composite thunk so the post-submit verify cycle (and Scope=1
  // recovery via no-op re-submit) runs before navigation. See
  // docs/allocation-api-flow.md "Recovery Flow".
  const submitAllocation = async () => {
    const lines = (allocationLines || [])
      .filter((l) => l.orderOpenQty > 0)
      .map((l) => ({
        lineNumber: l.lineNumber,
        allocateQty: Math.min(l.orderOpenQty, l.availableToAllocate),
      }))
    const result = await dispatch(
      doSubmitAndVerifyAllocation({
        orderId,
        role: mockRole,
        username: 'J. Anderson',
        lines,
        org: mockOrg,
      }),
    )
    return result
  }

  const handleAllocateAndClose = async () => {
    const result = await submitAllocation()
    if (!result.error) {
      dispatch(
        setSnackbar({
          message: 'Allocation submitted',
          severity: 'success',
          action: true,
          autoHideDuration: 3000,
        }),
      )
      navigate('/hold-allocation')
    }
  }

  const handleAllocateAndNext = async () => {
    const result = await submitAllocation()
    if (!result.error) {
      dispatch(
        setSnackbar({
          message: 'Allocation submitted — loading next order',
          severity: 'success',
          action: true,
          autoHideDuration: 3000,
        }),
      )
      // Find next order in same bucket
      try {
        const res = await getOrders({ role: mockRole, org: mockOrg, bucket })
        const orderList = res.data?.data || []
        const currentIdx = orderList.findIndex((o) => o.orderId === orderId)
        const nextOrder = orderList[currentIdx + 1] || orderList[0]
        if (nextOrder && nextOrder.orderId !== orderId) {
          navigate(`/hold-allocation/${nextOrder.orderId}?bucket=${bucket}`)
        } else {
          navigate('/hold-allocation')
        }
      } catch {
        navigate('/hold-allocation')
      }
    }
  }

  // ─── Determine if allocation section should show ─────
  const openHolds = holds.filter((h) => !h.released)
  const releasableHolds = openHolds.filter((h) => h.releasable)
  const showAllocation =
    (holds.length === 0) || // Unheld order (READY_TO_ALLOCATE) — show allocation directly
    (releasableHolds.length === 0 && holds.some((h) => h.released)) // All releasable holds released

  // Lazy-load enriched allocation lines (with activeStock/totalOpenQty from
  // inventory SOAP) when the allocation panel becomes visible.
  useEffect(() => {
    if (showAllocation && orderId) {
      dispatch(doPrefillAllocation({ orderId, org: mockOrg }))
    }
  }, [showAllocation, orderId, dispatch, mockOrg])

  // Format created date
  const formatCreatedDate = (dt) => {
    if (!dt) return ''
    const d = new Date(dt)
    return `${d.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    })} ${d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`
  }

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#f2f5f2', minHeight: '100vh' }}>
      <HoldAllocationNav mockRole={mockRole} onRoleChange={setMockRole} mockOrg={mockOrg} onOrgChange={setMockOrg} />
      <Backdrop open={isFetching} sx={{ zIndex: 9999 }}>
        <CircularProgress color='inherit' />
      </Backdrop>

      <Container maxWidth='xl' sx={{ pt: 3, pb: 8 }}>
        {/* ─── Header ───────────────────────────────────── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            mb: 0.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography sx={{ fontFamily: 'SanomatMedium', fontSize: '2rem', color: '#1a8a6a' }}>
              Order - {orderId}
            </Typography>
            {orderDetail && orderDetail.orderStatus === 'ON_HOLD' && (
              <Box
                sx={{
                  px: 1.5,
                  py: 0.4,
                  backgroundColor: '#fde9a7',
                  color: '#8a5a00',
                  borderRadius: 1,
                  fontSize: '0.82rem',
                  fontFamily: 'AtlasGroteskRegular',
                  fontWeight: 700,
                  border: '1px solid #f5d98a',
                }}
              >
                Hold
              </Box>
            )}
          </Box>
          {orderDetail && (
            <Box sx={{ textAlign: 'right' }}>
              <Typography
                sx={{
                  fontFamily: 'AtlasGroteskRegular',
                  fontSize: '0.78rem',
                  fontWeight: 700,
                  color: '#262626',
                }}
              >
                Created on
              </Typography>
              <Typography
                sx={{ fontFamily: 'AtlasGroteskRegular', fontSize: '0.82rem', color: '#262626' }}
              >
                {formatCreatedDate(orderDetail.createdAt)}
              </Typography>
            </Box>
          )}
        </Box>
        {orderDetail && orderDetail.orderStatus === 'ON_HOLD' && (
          <Typography
            sx={{ fontFamily: 'AtlasGroteskRegular', fontSize: '0.85rem', color: '#606060', mb: 3 }}
          >
            The order is currently on hold and is waiting to be released.
          </Typography>
        )}

        {/* ─── Three cards row ──────────────────────────── */}
        {orderDetail && (
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mb: 2.5 }}>
            <BillingDetailsCardV2 orderDetail={orderDetail} />
            <ShippingDetailsCardV2 orderDetail={orderDetail} />
            <OrderSummaryCardV2 orderDetail={orderDetail} />
          </Box>
        )}

        {/* ─── Ship Instructions (conditional) ──────────── */}
        {orderDetail && orderDetail.shipInstructions && (
          <Box sx={{ mb: 2.5 }}>
            <ShipInstructionsBarV2
              shipMethod={orderDetail.shipMethod}
              shipInstructions={orderDetail.shipInstructions}
            />
          </Box>
        )}

        {/* ─── Customer Credit Info ─────────────────────── */}
        {orderDetail && (
          <Box sx={{ mb: 3 }}>
            <CustomerCreditInfoBarV2 orderDetail={orderDetail} />
          </Box>
        )}

        {/* ─── Order Line Details ───────────────────────── */}
        {orderDetail && orderDetail.lines && orderDetail.lines.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <OrderLineDetailsTableV2 lines={orderDetail.lines} />
          </Box>
        )}

        {/* ─── Hold Details ─────────────────────────────── */}
        {holds.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <HoldDetailsTableV2
              holds={holds}
              onRelease={handleReleaseSingle}
              onReleaseAll={handleReleaseAll}
              onReapply={handleReapplyHold}
              isSubmitting={isSubmitting}
            />
          </Box>
        )}

        {/* ─── Allocation Details (after release) ───────── */}
        {showAllocation && allocationLines && allocationLines.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <AllocationDetailsTableV2
              allocationLines={allocationLines}
              onAllocateAndClose={handleAllocateAndClose}
              onAllocateAndNext={handleAllocateAndNext}
              isSubmitting={isSubmitting}
            />
          </Box>
        )}
      </Container>

      <GenericSnackbar />
    </Box>
  )
}

export default HoldAllocationDetailV2
